<script lang="typescript">
  import { onMount } from "svelte";
  // @ts-nocheck
  import { Circle } from "svelte-loading-spinners";
  import {
    createJWTWithCode,
    loginWithJWTToken,
    logout,
    redirectToAuthorizationFlow,
    retrieveAuthorizationCodeFromQueryString,
    userStore,
  } from "./auth";
  import AccountSelect from "./components/AccountSelect.svelte";
  import { createJob, createJobStore } from "./jobs";

  import Layout from "./Layout.svelte";
  import Button from "./components/Button.svelte";
  import CreateList from "./components/CreateList.svelte";
  import CreateStep from "./components/CreateStep.svelte";
  import CrypotInput from "./components/CrypotInput.svelte";
  import JobList from "./components/JobList.svelte";
  import Feedback from "./components/Feedback.svelte";
  import Trust from "./components/Trust.svelte";
  import { Check, ShieldCheck } from "svelte-hero-icons";

  /**
   * the code, if set, is a authorization code
   * after we are loaded as part of a oauth redirection flow.
   * the code needs to be sent to the server to authenticate us
   * and get us a firebase auth token
   */
  let code: string | undefined | null;
  /**
   * after we got a authorization code we generate a jwt on the backend
   * with which we can now login (just once)
   */
  let jwt: string | undefined;

  let selectedBudget: string | null;
  let selectedAccount: string | null;
  let selectedCurrency: string | null;
  let address: string | undefined;
  let symbol: "ETH" | "BTC" | undefined;
  let addressIsValid: boolean;
  let lastDone: number = 0;
  let jobStatus: "default" | "creating" | "created" = "default";
  let jobCreatorExpanded: boolean = false;
  let usedAccountIds: string[] = [];
  let jobStore = createJobStore();

  const handleCreateJob = () => {
    if (selectedBudget && selectedAccount && symbol && address) {
      jobStatus = "creating";
      createJob({
        budgetId: selectedBudget,
        accountId: selectedAccount,
        address,
        symbol,
        currency: selectedCurrency!,
      })
        .then(() => {
          jobStatus = "created";
        })
        .catch(console.error);
    }
  };

  /**
   * on mount, see if there is a authorization code in the url
   */
  onMount(() => {
    if (!jwt) {
      // note: if code found in url, it will be removed from url
      // using location replace, as it will be now unusable.
      code = retrieveAuthorizationCodeFromQueryString();
      if (code) {
        document
          .getElementById("app-main")
          ?.scrollIntoView({ behavior: "smooth" });
      }
    }
  });

  // whenever the code changes, we require a jwt token for login
  // this also basically registers the ynab user (if not already)
  $: if (code) {
    createJWTWithCode(code).then((newJWT) => {
      jwt = newJWT;
      // reset the code, as unusable now
      code = undefined;
    });
  }

  // login the user as soon as we get a jwt token
  $: if (jwt && !$userStore) {
    loginWithJWTToken(jwt).then(() => {
      jwt = undefined;
    });
  }

  $: {
    lastDone = 0;
    if ($userStore) {
      lastDone = 1;
      if (selectedBudget && selectedAccount) {
        lastDone = 2;
      }

      if (address && symbol && addressIsValid) {
        lastDone = 3;
      }
    }
  }

  $: if ($jobStore) {
    usedAccountIds = Object.values($jobStore).map((j) => j.account.accountId);
  }

  function resetCreator() {
    selectedBudget = selectedAccount = null;
    symbol = address = undefined;
    lastDone = 0;
    jobStatus = "default";
  }
</script>

<style global>
  @tailwind base;
  @tailwind components;
  @tailwind utilities;

  html {
    scroll-behavior: smooth;
  }
</style>

<Layout>
  <h2 class="font-extrabold text-2xl mb-4 mt-8">
    Setup a crypto wallet importer
  </h2>
  <div class="my-2">
    Let me now, if something isn't right, or if you'd like to improve something
    <Feedback />
  </div>
  <div class="my-3">
    <Trust />
  </div>

  <div class="mt-5 flex-col shadow-lg border rounded-lg">
    <CreateList>
      <CreateStep
        state={lastDone >= 1 ? 'done' : 'active'}
        title={lastDone >= 1 ? 'You are logged in! 👍' : 'Please login with YNAB'}
        stepNumber={1}>
        {#if lastDone === 0}
          {#if jwt || code || $userStore === undefined}
            <Circle
              size="24"
              color="#3b83f6"
              unit="px"
              duration="1s"
              class="mr-2" />
          {:else}
            <Button on:click={redirectToAuthorizationFlow}>
              Login with YNAB
            </Button>
            <div class="my-3 text-sm text-gray-500">
              Read the
              <a href="#privacy" class="underline">Privacy policy</a>
              at the bottom of the page
            </div>
          {/if}
        {/if}
      </CreateStep>
      <CreateStep
        title={lastDone >= 2 ? 'Target account selected 🙌' : 'Where do yo want to sync your balance to?'}
        stepNumber={2}
        state={lastDone >= 2 ? 'done' : lastDone === 1 ? 'active' : 'default'}>
        {#if lastDone === 1}
          <AccountSelect
            bind:selectedAccount
            bind:selectedBudget
            bind:selectedCurrency
            blacklistedAccountIds={usedAccountIds} />
        {/if}
      </CreateStep>
      <CreateStep
        title={lastDone >= 3 ? 'Crypto wallet selected! 🎉' : 'From which crypto wallet do you want to sync your balance?'}
        state={lastDone >= 3 ? 'done' : lastDone === 2 ? 'active' : 'default'}
        stepNumber={3}>
        {#if lastDone === 2}
          <CrypotInput
            bind:currency={selectedCurrency}
            bind:address
            bind:symbol
            bind:isValid={addressIsValid} />
        {/if}
      </CreateStep>
    </CreateList>

    <div class="bg-gray-100 border-t border-gray-200">
      <CreateList>
        <CreateStep
          state={lastDone === 3 ? 'active' : 'default'}
          stepNumber={4}>
          {#if jobStatus === 'default'}
            <Button on:click={handleCreateJob} disabled={lastDone < 3}>
              Create Sync Job
            </Button>
          {:else if jobStatus === 'creating'}
            <Circle
              size="24"
              color="#3b83f6"
              unit="px"
              duration="1s"
              class="mr-2" />
          {:else if jobStatus === 'created'}
            Hurray, created!
            <Button on:click={resetCreator}>Create New Importer</Button>
          {/if}
        </CreateStep>
      </CreateList>
    </div>
  </div>

  <div class="mt-6">
    <h2 class="font-extrabold text-2xl mb-4 mt-8">Your active importers</h2>
    <JobList />
  </div>

  {#if $userStore}
    <div class="mt-6">
      <Button on:click={logout}>Logout</Button>
    </div>
  {/if}
</Layout>
